/**
 * Route Configuration - Centralized route definitions
 * Uses React.lazy for code splitting and faster initial load
 */
import React from 'react';

// Core Pages (loaded immediately)
import { LoginPage } from '../pages/LoginPage';
import { DashboardPage } from '../pages/DashboardPage';

// Lazy-loaded pages
const lazy = (fn) => React.lazy(fn);

// Core
const ClientsPage = lazy(() => import('../pages/ClientsPage').then(m => ({ default: m.ClientsPage || m.default })));
const ProjectsPage = lazy(() => import('../pages/ProjectsPage').then(m => ({ default: m.ProjectsPage || m.default })));
const InvoicesPage = lazy(() => import('../pages/InvoicesPage').then(m => ({ default: m.InvoicesPage || m.default })));
const ExpensesPage = lazy(() => import('../pages/ExpensesPage').then(m => ({ default: m.ExpensesPage || m.default })));
const EmployeesPage = lazy(() => import('../pages/EmployeesPage').then(m => ({ default: m.EmployeesPage || m.default })));
const ServicesPage = lazy(() => import('../pages/ServicesPage').then(m => ({ default: m.ServicesPage || m.default })));
const ReportsPage = lazy(() => import('../pages/ReportsPage').then(m => ({ default: m.ReportsPage || m.default })));
const UsersManagementPage = lazy(() => import('../pages/UsersManagementPage').then(m => ({ default: m.UsersManagementPage || m.default })));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage').then(m => ({ default: m.NotificationsPage || m.default })));
const BackupPage = lazy(() => import('../pages/BackupPage').then(m => ({ default: m.BackupPage || m.default })));
const CompanySettingsPage = lazy(() => import('../pages/CompanySettingsPage').then(m => ({ default: m.CompanySettingsPage || m.default })));

// Business Pages
const DeliveryOrdersPage = lazy(() => import('../pages/DeliveryOrdersPage'));
const DeliveryDashboardPage = lazy(() => import('../pages/DeliveryDashboardPage'));
const DriverTrackingPage = lazy(() => import('../pages/DriverTrackingPage'));
const DriverPerformancePage = lazy(() => import('../pages/DriverPerformancePage'));
const DriverLeaderboardPage = lazy(() => import('../pages/DriverLeaderboardPage'));
const ReferralAnalyticsPage = lazy(() => import('../pages/ReferralAnalyticsPage'));
const ReferralChallengeAdminPage = lazy(() => import('../pages/ReferralChallengeAdminPage'));
const CustomerTrackingPage = lazy(() => import('../pages/CustomerTrackingPage'));
const CashCollectionPage = lazy(() => import('../pages/CashCollectionPage'));
const ZoneManagementPage = lazy(() => import('../pages/ZoneManagementPage'));
const FleetManagementPage = lazy(() => import('../pages/FleetManagementPage'));
const DeliveryPhotosPage = lazy(() => import('../pages/DeliveryPhotosPage'));
const DriverRatingsPage = lazy(() => import('../pages/DriverRatingsPage'));
const PaymentTransactionsPage = lazy(() => import('../pages/PaymentTransactionsPage'));
const NotificationSettingsPage = lazy(() => import('../pages/NotificationSettingsPage'));
const InvoiceRemindersPage = lazy(() => import('../pages/InvoiceRemindersPage'));
const AdvancedFinancialReportsPage = lazy(() => import('../pages/AdvancedFinancialReportsPage'));
const QuotationsPage = lazy(() => import('../pages/QuotationsPage'));
const ContractsPage = lazy(() => import('../pages/ContractsPage'));
const InventoryPage = lazy(() => import('../pages/InventoryPage'));
const PayrollPage = lazy(() => import('../pages/PayrollPage'));
const AttendancePage = lazy(() => import('../pages/AttendancePage'));
const SuppliersPage = lazy(() => import('../pages/SuppliersPage'));
const AnalyticsPage = lazy(() => import('../pages/AnalyticsPage'));

// Extended Modules
const NotificationsSystemPage = lazy(() => import('../pages/NotificationsSystemPage'));
const CalendarPage = lazy(() => import('../pages/CalendarPage'));
const DesignsPage = lazy(() => import('../pages/DesignsPage'));
const CampaignsPage = lazy(() => import('../pages/CampaignsPage'));
const ReviewsPage = lazy(() => import('../pages/ReviewsPage'));
const BankAccountsPage = lazy(() => import('../pages/BankAccountsPage'));
const SocialMediaPage = lazy(() => import('../pages/SocialMediaPage'));
const OffersPage = lazy(() => import('../pages/OffersPage'));
const PaymentsPage = lazy(() => import('../pages/PaymentsPage'));
const AuditLogPage = lazy(() => import('../pages/AuditLogPage'));
const WhatsAppPage = lazy(() => import('../pages/WhatsAppPage'));
const EmailReportsPage = lazy(() => import('../pages/EmailReportsPage'));
const DocumentationPage = lazy(() => import('../pages/DocumentationPage'));
const SupportTicketsPage = lazy(() => import('../pages/SupportTicketsPage'));
const TaxesPage = lazy(() => import('../pages/TaxesPage'));
const TasksPage = lazy(() => import('../pages/TasksPage'));
const TimeTrackingPage = lazy(() => import('../pages/TimeTrackingPage'));
const LeadsPage = lazy(() => import('../pages/LeadsPage'));
const PurchaseOrdersPage = lazy(() => import('../pages/PurchaseOrdersPage'));
const ChartsAnalyticsPage = lazy(() => import('../pages/ChartsAnalyticsPage'));
const AssetsPage = lazy(() => import('../pages/AssetsPage'));
const EmailCampaignsPage = lazy(() => import('../pages/EmailCampaignsPage'));
const FilesPage = lazy(() => import('../pages/FilesPage'));
const CRMPage = lazy(() => import('../pages/CRMPage'));
const CompanyKPIsPage = lazy(() => import('../pages/CompanyKPIsPage'));
const BookingsPage = lazy(() => import('../pages/BookingsPage'));
const InstallmentsPage = lazy(() => import('../pages/InstallmentsPage'));
const ClientKPIsPage = lazy(() => import('../pages/ClientKPIsPage'));
const AnnualComparisonPage = lazy(() => import('../pages/AnnualComparisonPage'));
const ArchivePage = lazy(() => import('../pages/ArchivePage'));
const SuggestionsComplaintsPage = lazy(() => import('../pages/SuggestionsComplaintsPage'));
const LiveKPIPage = lazy(() => import('../pages/LiveKPIPage'));
const BrandGuidelinesPage = lazy(() => import('../pages/BrandGuidelinesPage'));
const MaintenanceSchedulePage = lazy(() => import('../pages/MaintenanceSchedulePage'));
const InternalReviewPage = lazy(() => import('../pages/InternalReviewPage'));
const SOPPage = lazy(() => import('../pages/SOPPage'));
const SmartPricingPage = lazy(() => import('../pages/SmartPricingPage'));
const OKRPage = lazy(() => import('../pages/OKRPage'));
const ProjectProfitabilityPage = lazy(() => import('../pages/ProjectProfitabilityPage'));

// Print Module
const PrintOrdersPage = lazy(() => import('../pages/PrintOrdersPage'));
const PrintSettingsPage = lazy(() => import('../pages/PrintSettingsPage'));
const PrintProfitReportsPage = lazy(() => import('../pages/PrintProfitReportsPage'));

// Management & Reports
const ApprovalsPage = lazy(() => import('../pages/ApprovalsPage'));
const DriversManagementPage = lazy(() => import('../pages/DriversManagementPage'));
const KanbanPage = lazy(() => import('../pages/KanbanPage'));
const ChatPage = lazy(() => import('../pages/ChatPage'));
const SecuritySettingsPage = lazy(() => import('../pages/SecuritySettingsPage'));
const AutoInvoicePage = lazy(() => import('../pages/AutoInvoicePage'));
const FinancialReportsPage = lazy(() => import('../pages/FinancialReportsPage'));
const SystemSettingsPage = lazy(() => import('../pages/SystemSettingsPage'));
const EmployeeKPIPage = lazy(() => import('../pages/EmployeeKPIPage'));
const ScheduledReportsPage = lazy(() => import('../pages/ScheduledReportsPage'));
const SmartAlertsPage = lazy(() => import('../pages/SmartAlertsPage'));
const ExecutiveDashboardPage = lazy(() => import('../pages/ExecutiveDashboardPage'));

// Design & Portfolio
const PortfolioPage = lazy(() => import('../pages/PortfolioPage'));
const DesignReviewsPage = lazy(() => import('../pages/DesignReviewsPage'));

// HR Module
const LeavesPage = lazy(() => import('../pages/LeavesPage'));
const RewardsPage = lazy(() => import('../pages/RewardsPage'));
const LeaveManagementPage = lazy(() => import('../pages/LeaveManagementPage'));
const LeaveReportsPage = lazy(() => import('../pages/LeaveReportsPage'));

// Advanced Features
const SubscriptionsPage = lazy(() => import('../pages/SubscriptionsPage'));
const AIAssistantPage = lazy(() => import('../pages/AIAssistantPage'));
const AIForecastPage = lazy(() => import('../pages/AIForecastPage'));
const AIDashboardPage = lazy(() => import('../pages/AIDashboardPage'));
const AIFeaturesPage = lazy(() => import('../pages/AIFeaturesPage'));
const InternalTicketsPage = lazy(() => import('../pages/InternalTicketsPage'));
const MeetingRoomsPage = lazy(() => import('../pages/MeetingRoomsPage'));
const ESignaturePage = lazy(() => import('../pages/ESignaturePage'));
const SMSPage = lazy(() => import('../pages/SMSPage'));
const PresentationsPage = lazy(() => import('../pages/PresentationsPage'));
const BranchesPage = lazy(() => import('../pages/BranchesPage'));
const UnifiedReportsPage = lazy(() => import('../pages/UnifiedReportsPage'));
const TeamPerformancePage = lazy(() => import('../pages/TeamPerformancePage'));
const ExpenseTrackingPage = lazy(() => import('../pages/ExpenseTrackingPage'));
const DigitalAssetsPage = lazy(() => import('../pages/DigitalAssetsPage'));
const ProjectTemplatesPage = lazy(() => import('../pages/ProjectTemplatesPage'));
const EmailMarketingPage = lazy(() => import('../pages/EmailMarketingPage'));
const CustomerSurveysPage = lazy(() => import('../pages/CustomerSurveysPage'));
const ResourcePlanningPage = lazy(() => import('../pages/ResourcePlanningPage'));
const AdvancedDashboard = lazy(() => import('../pages/AdvancedDashboard'));
const InternalChatPage = lazy(() => import('../pages/InternalChatPage'));
const PerformanceReportsPage = lazy(() => import('../pages/PerformanceReportsPage'));
const UserExperiencePage = lazy(() => import('../pages/UserExperiencePage'));
const BusinessManagementPage = lazy(() => import('../pages/BusinessManagementPage'));
const SecurityIntegrationPage = lazy(() => import('../pages/SecurityIntegrationPage'));

// Portal & Driver
const CustomerPortalLogin = lazy(() => import('../pages/portal/CustomerPortalLogin'));
const CustomerPortalDashboardModule = lazy(() => import('../pages/portal/CustomerPortalDashboard'));
const CustomerPortalInvoices = lazy(() => import('../pages/portal/CustomerPortalInvoices'));
const CustomerPortalTickets = lazy(() => import('../pages/portal/CustomerPortalTickets'));
const CustomerPortalAdvanced = lazy(() => import('../pages/portal/CustomerPortalAdvanced'));
const ClientPrintRequestsPage = lazy(() => import('../pages/ClientPrintRequestsPage'));
const DriverMobilePage = lazy(() => import('../pages/DriverMobilePage'));
const DriverLoginPage = lazy(() => import('../pages/DriverLoginPage'));
const PublicLeaderboardPage = lazy(() => import('../pages/PublicLeaderboardPage'));
const SSEMonitorPage = lazy(() => import('../pages/SSEMonitorPage'));
const OfficeServicesPage = lazy(() => import('../pages/OfficeServicesPage'));

// New ERP Modules
const TendersPage = lazy(() => import('../pages/TendersPage'));
const EventsManagementPage = lazy(() => import('../pages/EventsManagementPage'));
const OutdoorAdsPage = lazy(() => import('../pages/OutdoorAdsPage'));
const StudioBookingPage = lazy(() => import('../pages/StudioBookingPage'));
const InfluencersPage = lazy(() => import('../pages/InfluencersPage'));
const TrainingPage = lazy(() => import('../pages/TrainingPage'));
const GamificationPage = lazy(() => import('../pages/GamificationPage'));
const LoyaltyProgramPage = lazy(() => import('../pages/LoyaltyProgramPage'));
const IPManagementPage = lazy(() => import('../pages/IPManagementPage'));
const EquipmentPage = lazy(() => import('../pages/EquipmentPage'));
const ComplaintsPage = lazy(() => import('../pages/ComplaintsPage'));
const CommissionsPage = lazy(() => import('../pages/CommissionsPage'));
const ReferralsPage = lazy(() => import('../pages/ReferralsPage'));
const WebProjectsPage = lazy(() => import('../pages/WebProjectsPage'));
const VideoProductionPage = lazy(() => import('../pages/VideoProductionPage'));
const ContentManagementPage = lazy(() => import('../pages/ContentManagementPage'));
const TranslationPage = lazy(() => import('../pages/TranslationPage'));
const LaunchesPage = lazy(() => import('../pages/LaunchesPage'));

/**
 * Protected Routes - Require authentication
 */
export const protectedRoutes = [
  // Core
  { path: '/dashboard', component: DashboardPage },
  { path: '/clients', component: ClientsPage },
  { path: '/projects', component: ProjectsPage },
  { path: '/invoices', component: InvoicesPage },
  { path: '/payments', component: PaymentsPage },
  { path: '/expenses', component: ExpensesPage },
  { path: '/employees', component: EmployeesPage },
  { path: '/services', component: ServicesPage },
  { path: '/reports', component: ReportsPage },
  { path: '/email-reports', component: EmailReportsPage },
  { path: '/documentation', component: DocumentationPage },
  { path: '/support-tickets', component: SupportTicketsPage },
  { path: '/kanban', component: KanbanPage },
  { path: '/chat', component: ChatPage },
  { path: '/security-settings', component: SecuritySettingsPage },
  { path: '/auto-invoice', component: AutoInvoicePage },
  { path: '/taxes', component: TaxesPage },
  { path: '/users-management', component: UsersManagementPage },
  { path: '/notifications', component: NotificationsPage },
  { path: '/audit-log', component: AuditLogPage },
  { path: '/backups', component: BackupPage },
  { path: '/company-settings', component: CompanySettingsPage },
  
  // Delivery
  { path: '/delivery-orders', component: DeliveryOrdersPage },
  { path: '/delivery', component: DeliveryDashboardPage },
  { path: '/driver-tracking', component: DriverTrackingPage },
  { path: '/driver-performance', component: DriverPerformancePage },
  { path: '/driver-leaderboard', component: DriverLeaderboardPage },
  { path: '/referral-analytics', component: ReferralAnalyticsPage },
  { path: '/referral-challenge', component: ReferralChallengeAdminPage },
  { path: '/drivers-management', component: DriversManagementPage },
  { path: '/cash-collection', component: CashCollectionPage },
  { path: '/zones', component: ZoneManagementPage },
  { path: '/fleet', component: FleetManagementPage },
  { path: '/delivery-photos', component: DeliveryPhotosPage },
  { path: '/driver-ratings', component: DriverRatingsPage },
  { path: '/payment-transactions', component: PaymentTransactionsPage },
  { path: '/customer-tracking', component: CustomerTrackingPage },
  { path: '/notification-settings', component: NotificationSettingsPage },
  { path: '/invoice-reminders', component: InvoiceRemindersPage },
  
  // Finance
  { path: '/quotations', component: QuotationsPage },
  { path: '/contracts', component: ContractsPage },
  { path: '/payroll', component: PayrollPage },
  { path: '/bank-accounts', component: BankAccountsPage },
  { path: '/offers', component: OffersPage },
  { path: '/installments', component: InstallmentsPage },
  { path: '/financial-reports', component: FinancialReportsPage },
  { path: '/advanced-financial-reports', component: AdvancedFinancialReportsPage },
  { path: '/annual-comparison', component: AnnualComparisonPage },
  { path: '/smart-pricing', component: SmartPricingPage },
  { path: '/project-profitability', component: ProjectProfitabilityPage },
  { path: '/expense-tracking', component: ExpenseTrackingPage },
  { path: '/subscriptions', component: SubscriptionsPage },
  
  // Inventory
  { path: '/inventory', component: InventoryPage },
  { path: '/suppliers', component: SuppliersPage },
  { path: '/purchase-orders', component: PurchaseOrdersPage },
  { path: '/assets', component: AssetsPage },
  { path: '/maintenance-schedule', component: MaintenanceSchedulePage },
  { path: '/equipment', component: EquipmentPage },
  
  // Print
  { path: '/print-orders', component: PrintOrdersPage },
  { path: '/print-settings', component: PrintSettingsPage },
  { path: '/print-profit-reports', component: PrintProfitReportsPage },
  { path: '/client-print-requests', component: ClientPrintRequestsPage },
  
  // HR
  { path: '/attendance', component: AttendancePage },
  { path: '/leaves', component: LeavesPage },
  { path: '/leave-management', component: LeaveManagementPage },
  { path: '/leave-reports', component: LeaveReportsPage },
  { path: '/rewards', component: RewardsPage },
  { path: '/employee-kpi', component: EmployeeKPIPage },
  { path: '/training', component: TrainingPage },
  { path: '/internal-review', component: InternalReviewPage },
  { path: '/sop', component: SOPPage },
  { path: '/gamification', component: GamificationPage },
  { path: '/commissions', component: CommissionsPage },
  
  // Marketing
  { path: '/campaigns', component: CampaignsPage },
  { path: '/email-campaigns', component: EmailCampaignsPage },
  { path: '/email-marketing', component: EmailMarketingPage },
  { path: '/social-media', component: SocialMediaPage },
  { path: '/whatsapp', component: WhatsAppPage },
  { path: '/sms', component: SMSPage },
  { path: '/influencers', component: InfluencersPage },
  { path: '/customer-surveys', component: CustomerSurveysPage },
  
  // Clients & Projects
  { path: '/leads', component: LeadsPage },
  { path: '/crm', component: CRMPage },
  { path: '/reviews', component: ReviewsPage },
  { path: '/client-kpis', component: ClientKPIsPage },
  { path: '/brand-guidelines', component: BrandGuidelinesPage },
  { path: '/suggestions-complaints', component: SuggestionsComplaintsPage },
  { path: '/bookings', component: BookingsPage },
  { path: '/okr', component: OKRPage },
  { path: '/tenders', component: TendersPage },
  { path: '/events', component: EventsManagementPage },
  { path: '/outdoor-ads', component: OutdoorAdsPage },
  { path: '/studio-booking', component: StudioBookingPage },
  { path: '/complaints', component: ComplaintsPage },
  { path: '/referrals', component: ReferralsPage },
  { path: '/loyalty', component: LoyaltyProgramPage },
  
  // Design & Content
  { path: '/designs', component: DesignsPage },
  { path: '/design-reviews', component: DesignReviewsPage },
  { path: '/portfolio', component: PortfolioPage },
  { path: '/digital-assets', component: DigitalAssetsPage },
  { path: '/web-projects', component: WebProjectsPage },
  { path: '/video-production', component: VideoProductionPage },
  { path: '/content-management', component: ContentManagementPage },
  { path: '/translation', component: TranslationPage },
  { path: '/launches', component: LaunchesPage },
  { path: '/presentations', component: PresentationsPage },
  { path: '/ip-management', component: IPManagementPage },
  
  // Reports & Analytics
  { path: '/analytics', component: AnalyticsPage },
  { path: '/charts-analytics', component: ChartsAnalyticsPage },
  { path: '/unified-reports', component: UnifiedReportsPage },
  { path: '/team-performance', component: TeamPerformancePage },
  { path: '/performance-reports', component: PerformanceReportsPage },
  { path: '/scheduled-reports', component: ScheduledReportsPage },
  { path: '/executive-dashboard', component: ExecutiveDashboardPage },
  { path: '/advanced-dashboard', component: AdvancedDashboard },
  { path: '/live-kpi', component: LiveKPIPage },
  { path: '/company-kpis', component: CompanyKPIsPage },
  
  // AI Tools
  { path: '/ai-assistant', component: AIAssistantPage },
  { path: '/ai-forecast', component: AIForecastPage },
  { path: '/ai-dashboard', component: AIDashboardPage },
  { path: '/ai-features', component: AIFeaturesPage },
  
  // Tools & System
  { path: '/tasks', component: TasksPage },
  { path: '/time-tracking', component: TimeTrackingPage },
  { path: '/calendar', component: CalendarPage },
  { path: '/archive', component: ArchivePage },
  { path: '/files', component: FilesPage },
  { path: '/project-templates', component: ProjectTemplatesPage },
  { path: '/resource-planning', component: ResourcePlanningPage },
  { path: '/approvals', component: ApprovalsPage },
  { path: '/e-signature', component: ESignaturePage },
  { path: '/smart-alerts', component: SmartAlertsPage },
  { path: '/notifications-system', component: NotificationsSystemPage },
  { path: '/internal-tickets', component: InternalTicketsPage },
  { path: '/meeting-rooms', component: MeetingRoomsPage },
  { path: '/internal-chat', component: InternalChatPage },
  { path: '/branches', component: BranchesPage },
  { path: '/system-settings', component: SystemSettingsPage },
  { path: '/sse-monitor', component: SSEMonitorPage },
  { path: '/office-services', component: OfficeServicesPage },
  { path: '/user-experience', component: UserExperiencePage },
  { path: '/business-management', component: BusinessManagementPage },
  { path: '/security-integration', component: SecurityIntegrationPage },
];

/**
 * Public Routes
 */
export const publicRoutes = [
  { path: '/login', component: LoginPage },
  { path: '/customer-tracking/:orderId', component: CustomerTrackingPage },
  { path: '/driver-login', component: DriverLoginPage },
  { path: '/driver-mobile', component: DriverMobilePage },
  { path: '/leaderboard', component: PublicLeaderboardPage },
];

/**
 * Portal Routes
 */
export const portalRoutes = {
  layout: { path: '/portal', component: React.lazy(() => import('../pages/portal/CustomerPortalDashboard').then(m => ({ default: m.CustomerPortalLayout }))) },
  login: { path: '/portal/login', component: CustomerPortalLogin },
  children: [
    { path: 'dashboard', component: React.lazy(() => import('../pages/portal/CustomerPortalDashboard')) },
    { path: 'invoices', component: CustomerPortalInvoices },
    { path: 'tickets', component: CustomerPortalTickets },
    { path: 'designs', component: CustomerPortalAdvanced },
    { path: 'print-requests', component: ClientPrintRequestsPage },
    { path: 'projects', component: CustomerPortalAdvanced },
    { path: 'quotations', component: CustomerPortalAdvanced },
    { path: 'tracking', component: CustomerPortalAdvanced },
  ]
};

/**
 * Default Route
 */
export const defaultRoute = '/dashboard';

